import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Package, Trash2, Edit, CheckCircle, Archive, ArrowLeft, Calculator, Euro, Eye, Save, Send, FileText, CreditCard, DollarSign, Receipt, Loader2, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useTeamPermissions } from '../contexts/TeamPermissionsContext';
import ExcelImportModal from '../components/ExcelImportModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCIES = ['EUR', 'USD', 'GBP', 'INR', 'AUD', 'CAD'];
const EXPENSE_TYPES = [
  "Transportation",
  "Accommodation", 
  "Guide",
  "Adventure – Safari",
  "Food",
  "Miscellaneous",
  "Contingency",
  "Profit",
  "Others"
];
const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Card', 'Check', 'Other'];
const PAYMENT_STATUSES = ['Pending', 'Partial', 'Paid', 'Overdue', 'Refunded', 'Cancelled'];

const Sales = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canEdit } = useTeamPermissions();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [viewingPackage, setViewingPackage] = useState(null);
  const [documentStatus, setDocumentStatus] = useState({});
  const [useBottomUp, setUseBottomUp] = useState(false);
  const [costElements, setCostElements] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Payment tracking state
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('details');
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Bank Transfer'
  });
  
  const [formData, setFormData] = useState({
    sale_value: '',
    expected_cost: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    currency: 'EUR',
  });

  useEffect(() => {
    fetchPackages();
    // Restore draft form data from localStorage if exists
    const savedDraft = localStorage.getItem('sales_form_draft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        if (draft.formData) setFormData(draft.formData);
        if (draft.costElements) setCostElements(draft.costElements);
        if (draft.useBottomUp !== undefined) setUseBottomUp(draft.useBottomUp);
        if (draft.showCreateForm) setShowCreateForm(true);
      } catch (e) {
        console.error('Failed to restore draft:', e);
      }
    }
  }, []);

  // Auto-save form data to localStorage when editing
  useEffect(() => {
    if (showCreateForm && !editingPackage) {
      const hasData = formData.client_name || formData.client_email || formData.client_phone || 
                      formData.sale_value || formData.expected_cost || costElements.length > 0;
      if (hasData) {
        localStorage.setItem('sales_form_draft', JSON.stringify({
          formData,
          costElements,
          useBottomUp,
          showCreateForm: true
        }));
      }
    }
  }, [formData, costElements, useBottomUp, showCreateForm, editingPackage]);

  // Auto-calculate sale value and expected cost from cost elements in bottom-up mode
  useEffect(() => {
    if (useBottomUp && costElements.length > 0) {
      // Sale Value = Sum of ALL cost elements (including Contingency & Profit)
      const saleValue = costElements.reduce((sum, elem) => sum + (parseFloat(elem.value) || 0), 0);
      // Expected Cost (Planned Cost) = Sum of cost elements EXCLUDING Contingency & Profit
      const expectedCost = costElements
        .filter(elem => !['Contingency', 'Profit'].includes(elem.expense_type))
        .reduce((sum, elem) => sum + (parseFloat(elem.value) || 0), 0);
      setFormData(prev => ({ 
        ...prev, 
        sale_value: saleValue.toString(),
        expected_cost: expectedCost.toString()
      }));
    }
  }, [costElements, useBottomUp]);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/packages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPackages(response.data);
      
      const statusPromises = response.data.map(pkg =>
        axios.get(`${API}/packages/${pkg.package_id}/document-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => ({ package_id: pkg.package_id, status: res.data }))
        .catch(() => ({ package_id: pkg.package_id, status: {} }))
      );
      
      const statuses = await Promise.all(statusPromises);
      const statusMap = {};
      statuses.forEach(s => {
        statusMap[s.package_id] = s.status;
      });
      setDocumentStatus(statusMap);
    } catch (error) {
      console.error('Error fetching packages:', error);
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Please verify your email to access this feature');
      }
    } finally {
      setLoading(false);
    }
  };

  // Payment tracking functions
  const fetchPaymentData = async (packageId) => {
    if (!packageId) return;
    
    setLoadingPayments(true);
    try {
      const token = localStorage.getItem('auth_token');
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`${API}/payments/packages/${packageId}/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/payments/packages/${packageId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPaymentSummary(summaryRes.data);
      setPaymentHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching payment data:', error);
      // Set default values if no payments exist yet
      setPaymentSummary({
        total_amount: viewingPackage?.sale_value || 0,
        amount_paid: 0,
        balance_due: viewingPackage?.sale_value || 0,
        payment_status: 'Pending',
        payment_count: 0,
        currency: viewingPackage?.currency || 'EUR'
      });
      setPaymentHistory([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setAddingPayment(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API}/payments/packages/${viewingPackage.package_id}`,
        {
          amount: parseFloat(paymentForm.amount),
          payment_date: paymentForm.payment_date,
          payment_method: paymentForm.payment_method
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payment recorded successfully');
      setShowAddPaymentDialog(false);
      setPaymentForm({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Bank Transfer'
      });
      
      // Refresh payment data for the modal
      fetchPaymentData(viewingPackage.package_id);
      // Also refresh the main packages list to update payment status in table
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setAddingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${API}/payments/packages/${viewingPackage.package_id}/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payment deleted');
      fetchPaymentData(viewingPackage.package_id);
      // Also refresh the main packages list to update payment status in table
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Partial': return 'bg-yellow-100 text-yellow-800';
      case 'Pending': return 'bg-gray-100 text-gray-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      case 'Refunded': return 'bg-purple-100 text-purple-800';
      case 'Cancelled': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Fetch payment data when viewing package changes
  useEffect(() => {
    if (viewingPackage) {
      setActiveDetailTab('details');
      fetchPaymentData(viewingPackage.package_id);
    }
  }, [viewingPackage?.package_id]);

  const addCostElement = () => {
    setCostElements([...costElements, {
      expense_type: '',
      service_provider: '',
      location: '',
      value: '',
      others_specification: ''
    }]);
  };

  const removeCostElement = (index) => {
    setCostElements(costElements.filter((_, i) => i !== index));
  };

  const updateCostElement = (index, field, value) => {
    const updated = [...costElements];
    updated[index][field] = value;
    setCostElements(updated);
  };

  const calculateTotalCost = () => {
    return costElements.reduce((sum, elem) => sum + (parseFloat(elem.value) || 0), 0);
  };

  const calculateProfit = () => {
    return costElements
      .filter(elem => elem.expense_type === 'Profit')
      .reduce((sum, elem) => sum + (parseFloat(elem.value) || 0), 0);
  };

  const calculateContingency = () => {
    return costElements
      .filter(elem => elem.expense_type === 'Contingency')
      .reduce((sum, elem) => sum + (parseFloat(elem.value) || 0), 0);
  };

  const calculateActualCosts = () => {
    return costElements
      .filter(elem => !['Profit', 'Contingency'].includes(elem.expense_type))
      .reduce((sum, elem) => sum + (parseFloat(elem.value) || 0), 0);
  };

  const resetForm = () => {
    setFormData({
      sale_value: '',
      expected_cost: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      currency: 'EUR',
    });
    setUseBottomUp(false);
    setCostElements([]);
    setEditingPackage(null);
    setShowCreateForm(false);
    // Clear the draft from localStorage
    localStorage.removeItem('sales_form_draft');
  };

  const validateForm = () => {
    if (!formData.client_name || !formData.client_email || !formData.client_phone) {
      toast.error('Please fill in all client information');
      return false;
    }
    if (useBottomUp && costElements.length === 0) {
      toast.error('Please add at least one cost element');
      return false;
    }
    if (useBottomUp) {
      const invalidElements = costElements.filter(elem => !elem.expense_type || !elem.value);
      if (invalidElements.length > 0) {
        toast.error('All cost elements must have an expense type and value');
        return false;
      }
    }
    if (!useBottomUp && (!formData.sale_value || !formData.expected_cost)) {
      toast.error('Please enter sale value and expected cost');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      
      // For bottom-up: expected_cost = sum of elements EXCLUDING Contingency & Profit
      const expectedCostValue = useBottomUp 
        ? calculateActualCosts() // This already excludes Contingency & Profit
        : parseFloat(formData.expected_cost);
      
      const payload = {
        ...formData,
        sale_value: parseFloat(formData.sale_value),
        expected_cost: expectedCostValue,
        use_bottom_up: useBottomUp,
        cost_elements: useBottomUp ? costElements.map(elem => ({
          expense_type: elem.expense_type,
          service_provider: elem.service_provider || null,
          location: elem.location || null,
          value: parseFloat(elem.value),
          others_specification: elem.others_specification || null
        })) : null,
        status: saveAsDraft ? 'draft' : 'active'
      };

      if (editingPackage) {
        await axios.put(
          `${API}/packages/${editingPackage.package_id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Package updated successfully!');
      } else {
        await axios.post(
          `${API}/packages`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(saveAsDraft ? 'Package saved as draft!' : 'Package created and activated!');
      }
      
      resetForm();
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    }
  };

  const handleStatusChange = async (packageId, newStatus) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.patch(
        `${API}/packages/${packageId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Package ${newStatus === 'active' ? 'activated' : newStatus === 'completed' ? 'completed' : 'updated'}!`);
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const openEditForm = (pkg) => {
    if (pkg.status !== 'draft') {
      toast.error('Only draft packages can be edited');
      return;
    }
    setEditingPackage(pkg);
    setFormData({
      sale_value: pkg.sale_value?.toString() || '',
      expected_cost: pkg.expected_cost?.toString() || '',
      client_name: pkg.client_name || '',
      client_email: pkg.client_email || '',
      client_phone: pkg.client_phone || '',
      currency: pkg.currency || 'EUR',
    });
    setUseBottomUp(pkg.use_bottom_up || false);
    setCostElements(pkg.cost_elements?.map(elem => ({
      expense_type: elem.expense_type,
      service_provider: elem.service_provider || '',
      location: elem.location || '',
      value: elem.value?.toString() || '',
      others_specification: elem.others_specification || ''
    })) || []);
    setShowCreateForm(true);
  };

  const openViewDialog = (pkg) => {
    setViewingPackage(pkg);
  };

  const handleArchivePackage = async (packageId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.patch(`${API}/packages/${packageId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Package archived successfully');
      fetchPackages();
    } catch (error) {
      toast.error('Failed to archive package');
    }
  };

  const handleDeletePackage = async (packageId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Package moved to trash');
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { variant: 'secondary', icon: Edit, label: 'Draft', color: 'text-gray-600' },
      active: { variant: 'default', icon: CheckCircle, label: 'Active', color: 'text-green-600' },
      completed: { variant: 'outline', icon: Archive, label: 'Completed', color: 'text-blue-600' }
    };
    const { variant, icon: Icon, label, color } = config[status] || config.draft;
    return (
      <Badge variant={variant} className={`flex items-center gap-1 ${color}`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="text-muted-foreground">{t('common.loading')}</div>;
  }

  // Full-page package creation form
  if (showCreateForm) {
    return (
      <div className="space-y-6" data-testid="package-create-form">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={resetForm}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Packages
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-extrabold text-foreground">
              {editingPackage ? 'Edit Tour Package' : 'Create New Tour Package'}
            </h1>
            <p className="text-muted-foreground">Enter client details and package pricing</p>
          </div>
        </div>

        <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
          {/* Client Information Card */}
          <Card>
            <CardHeader>
              <CardTitle>Client Information</CardTitle>
              <CardDescription>Enter the client details for this tour package</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="client_name">{t('packages.clientName')} *</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="Enter client name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_email">Client Email *</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    placeholder="client@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_phone">Client Phone *</Label>
                  <Input
                    id="client_phone"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    placeholder="+358 40 123 4567"
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Pricing Configuration</CardTitle>
              <CardDescription>Choose how to calculate package pricing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((currency) => (
                        <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-4 bg-muted/50 rounded-lg border">
                <Switch
                  id="bottom-up"
                  checked={useBottomUp}
                  onCheckedChange={(checked) => {
                    setUseBottomUp(checked);
                    if (!checked) {
                      setCostElements([]);
                      setFormData(prev => ({ ...prev, sale_value: '' }));
                    }
                  }}
                />
                <Label htmlFor="bottom-up" className="cursor-pointer flex-1">
                  <span className="font-semibold text-base">Bottom-Up Costing Mode</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    Build your package price by adding individual cost elements (Transportation, Accommodation, Profit, Contingency, etc.). 
                    The Sale Value will be automatically calculated.
                  </p>
                </Label>
              </div>

              {/* Top-Down Mode - Manual Entry */}
              {!useBottomUp && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="sale_value">{t('packages.saleValue')} ({formData.currency}) *</Label>
                    <Input
                      id="sale_value"
                      type="number"
                      step="0.01"
                      value={formData.sale_value}
                      onChange={(e) => setFormData({ ...formData, sale_value: e.target.value })}
                      placeholder="Enter sale value"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expected_cost">Expected Cost ({formData.currency}) *</Label>
                    <Input
                      id="expected_cost"
                      type="number"
                      step="0.01"
                      value={formData.expected_cost}
                      onChange={(e) => setFormData({ ...formData, expected_cost: e.target.value })}
                      placeholder="Enter expected cost"
                      required
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cost Elements Table - Bottom-Up Mode */}
          {useBottomUp && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="w-5 h-5" />
                      Cost Elements Builder
                    </CardTitle>
                    <CardDescription>Add each cost element to build your package price. Include Profit and Contingency as separate line items.</CardDescription>
                  </div>
                  <Button type="button" onClick={addCostElement} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Add Cost Element
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {costElements.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed rounded-lg">
                    <Calculator className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No cost elements added yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Click &quot;Add Cost Element&quot; to start building your package</p>
                    <Button type="button" onClick={addCostElement} variant="outline" className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Element
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-12">#</TableHead>
                            <TableHead className="min-w-[180px]">Expense Type *</TableHead>
                            <TableHead className="min-w-[150px]">Service Provider</TableHead>
                            <TableHead className="min-w-[150px]">Location</TableHead>
                            <TableHead className="min-w-[120px] text-right">Value ({formData.currency}) *</TableHead>
                            <TableHead className="min-w-[150px]">Notes</TableHead>
                            <TableHead className="w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {costElements.map((elem, index) => (
                            <TableRow key={index} className={['Profit', 'Contingency'].includes(elem.expense_type) ? 'bg-primary/5' : ''}>
                              <TableCell className="font-mono text-sm text-muted-foreground">{index + 1}</TableCell>
                              <TableCell>
                                <Select 
                                  value={elem.expense_type} 
                                  onValueChange={(value) => updateCostElement(index, 'expense_type', value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {EXPENSE_TYPES.map((type) => (
                                      <SelectItem key={type} value={type}>
                                        {['Profit', 'Contingency'].includes(type) ? `⭐ ${type}` : type}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                {!['Contingency', 'Profit'].includes(elem.expense_type) ? (
                                  <Input
                                    value={elem.service_provider}
                                    onChange={(e) => updateCostElement(index, 'service_provider', e.target.value)}
                                    placeholder="Provider name"
                                  />
                                ) : (
                                  <span className="text-muted-foreground text-sm">N/A</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {!['Contingency', 'Profit'].includes(elem.expense_type) ? (
                                  <Input
                                    value={elem.location}
                                    onChange={(e) => updateCostElement(index, 'location', e.target.value)}
                                    placeholder="Location"
                                  />
                                ) : (
                                  <span className="text-muted-foreground text-sm">N/A</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={elem.value}
                                  onChange={(e) => updateCostElement(index, 'value', e.target.value)}
                                  placeholder="0.00"
                                  className="text-right font-mono"
                                  required
                                />
                              </TableCell>
                              <TableCell>
                                {elem.expense_type === 'Others' ? (
                                  <Input
                                    value={elem.others_specification}
                                    onChange={(e) => updateCostElement(index, 'others_specification', e.target.value)}
                                    placeholder="Specify..."
                                    required
                                  />
                                ) : (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeCostElement(index)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Summary Panel */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg border">
                      <div className="p-4 bg-card rounded-lg border-2 border-orange-300">
                        <p className="text-xs text-orange-600 uppercase tracking-wider">Planned Cost</p>
                        <p className="text-2xl font-bold text-orange-700 font-mono">
                          {formData.currency} {calculateActualCosts().toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">Sale Value - Profit - Contingency</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-xs text-purple-600 uppercase tracking-wider">Planned Profit</p>
                        <p className="text-2xl font-bold text-purple-700 font-mono">
                          {formData.currency} {calculateProfit().toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs text-blue-600 uppercase tracking-wider">Contingency</p>
                        <p className="text-2xl font-bold text-blue-700 font-mono">
                          {formData.currency} {calculateContingency().toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
                        <p className="text-xs text-green-600 uppercase tracking-wider">Margin (Profit + Contingency)</p>
                        <p className="text-2xl font-bold text-green-700 font-mono">
                          {formData.currency} {(calculateProfit() + calculateContingency()).toLocaleString()}
                        </p>
                        <p className="text-xs text-green-600">{((calculateProfit() + calculateContingency()) / calculateTotalCost() * 100).toFixed(1)}% of Sale Value</p>
                      </div>
                    </div>
                    
                    {/* Sale Value Summary */}
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-400">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-700 font-medium flex items-center gap-1">
                            <Euro className="w-4 h-4" /> Total Sale Value
                          </p>
                          <p className="text-xs text-green-600">Planned Cost + Profit + Contingency</p>
                        </div>
                        <p className="text-3xl font-bold text-green-700 font-mono">
                          {formData.currency} {calculateTotalCost().toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Submit Buttons */}
          <div className="flex items-center gap-4">
            <Button type="button" variant="outline" size="lg" onClick={(e) => handleSubmit(e, true)}>
              <Save className="w-4 h-4 mr-2" />
              Save as Draft
            </Button>
            <Button type="submit" size="lg" className="px-8">
              <Send className="w-4 h-4 mr-2" />
              {editingPackage ? 'Update & Activate' : 'Create & Activate'}
            </Button>
            <Button type="button" variant="ghost" size="lg" onClick={resetForm}>
              Cancel
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground">
            <strong>Draft:</strong> Save and continue later. Not available for booking.<br/>
            <strong>Active:</strong> Package is complete and available for booking.
          </p>
        </form>
      </div>
    );
  }

  // Package List View
  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8" data-testid="sales-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold text-foreground tracking-tight">{t('packages.title')}</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">Create and manage tour packages with optional bottom-up costing</p>
        </div>
        {canEdit('sales') && (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button 
              data-testid="import-excel-button" 
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setShowImportModal(true)}
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Import from Excel
            </Button>
            <Button 
              data-testid="create-package-button" 
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto"
              onClick={() => setShowCreateForm(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('packages.createPackage')}
            </Button>
          </div>
        )}
      </div>

      {/* Excel Import Modal */}
      <ExcelImportModal 
        open={showImportModal} 
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          fetchPackages();
          setShowImportModal(false);
        }}
      />

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading font-semibold">All Tour Packages</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-6 sm:pt-0">
          {packages.length > 0 ? (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[800px]">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="whitespace-nowrap">{t('common.status')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('packages.docStatus')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('packages.packageId')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('packages.clientName')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('packages.packageType')}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t('packages.saleValue')}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t('packages.expectedCost')}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t('common.margin')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('common.currency')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {packages.map((pkg) => {
                    const margin = (pkg.sale_value || 0) - (pkg.expected_cost || 0);
                    const marginPercent = pkg.sale_value ? ((margin / pkg.sale_value) * 100).toFixed(1) : '0.0';
                    return (
                      <TableRow 
                        key={pkg.package_id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => openViewDialog(pkg)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>{getStatusBadge(pkg.status || 'draft')}</TableCell>
                        <TableCell>
                          <div className={`w-2.5 h-2.5 rounded-full ${documentStatus[pkg.package_id]?.has_sales_invoice ? 'bg-green-500' : 'bg-red-500'}`} />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{pkg.package_id}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{pkg.client_name}</p>
                            <p className="text-xs text-muted-foreground">{pkg.client_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={pkg.use_bottom_up ? 'default' : 'secondary'}>
                            {pkg.use_bottom_up ? 'Bottom-Up' : 'Top-Down'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">{pkg.currency} {(pkg.sale_value || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right font-mono">{pkg.currency} {(pkg.expected_cost || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <span className={margin >= 0 ? 'text-green-600' : 'text-red-500'}>
                            {pkg.currency} {margin.toLocaleString()} ({marginPercent}%)
                          </span>
                        </TableCell>
                        <TableCell><Badge variant="outline">{pkg.currency}</Badge></TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openViewDialog(pkg)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {canEdit('sales') && pkg.status === 'draft' && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openEditForm(pkg)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleStatusChange(pkg.package_id, 'active')}>
                                  Activate
                                </Button>
                              </>
                            )}
                            {canEdit('sales') && pkg.status === 'active' && (
                              <Button size="sm" variant="outline" onClick={() => handleStatusChange(pkg.package_id, 'completed')}>
                                Complete
                              </Button>
                            )}
                            {pkg.status === 'completed' && (
                              <span className="text-xs text-muted-foreground">Finalized</span>
                            )}
                            {canEdit('sales') && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => navigate(`/app/invoices?package_id=${pkg.package_id}`)}
                                title={t('invoice.createInvoice')}
                              >
                                <FileText className="w-4 h-4 text-primary" />
                              </Button>
                            )}
                            {canEdit('sales') && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleArchivePackage(pkg.package_id)}
                                title="Archive"
                              >
                                <Archive className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            )}
                            {canEdit('sales') && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleDeletePackage(pkg.package_id)}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No tour packages yet</p>
              <p className="text-sm mt-2">Create your first tour package</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Package Dialog */}
      <Dialog open={!!viewingPackage} onOpenChange={() => setViewingPackage(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Package className="w-5 h-5" />
              Package Details - {viewingPackage?.package_id}
              {viewingPackage && getStatusBadge(viewingPackage.status || 'draft')}
            </DialogTitle>
          </DialogHeader>
          
          {viewingPackage && (
            <Tabs value={activeDetailTab} onValueChange={setActiveDetailTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="details" className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Details
                </TabsTrigger>
                <TabsTrigger value="payments" className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Client Payments
                  {paymentSummary && (
                    <Badge variant="outline" className={`ml-1 ${getPaymentStatusColor(paymentSummary.payment_status)}`}>
                      {paymentSummary.payment_status}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              {/* Details Tab */}
              <TabsContent value="details" className="space-y-6">
                {/* Client Info */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Client Name</p>
                    <p className="font-semibold">{viewingPackage.client_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Email</p>
                    <p className="font-semibold">{viewingPackage.client_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Phone</p>
                    <p className="font-semibold">{viewingPackage.client_phone}</p>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground uppercase">Sale Value</p>
                      <p className="text-2xl font-bold text-green-600">
                        {viewingPackage.currency} {(viewingPackage.sale_value || 0).toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground uppercase">Expected Cost</p>
                      <p className="text-2xl font-bold">
                        {viewingPackage.currency} {(viewingPackage.expected_cost || 0).toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                  {viewingPackage.use_bottom_up && (
                    <>
                      <Card className="bg-purple-50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-purple-600 uppercase">Planned Profit</p>
                          <p className="text-2xl font-bold text-purple-700">
                            {viewingPackage.currency} {(viewingPackage.planned_profit || 0).toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-blue-600 uppercase">Contingency</p>
                          <p className="text-2xl font-bold text-blue-700">
                            {viewingPackage.currency} {(viewingPackage.planned_contingency || 0).toLocaleString()}
                          </p>
                          {viewingPackage.status === 'completed' && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Transferred to profit
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>

                {/* Cost Elements Table */}
                {viewingPackage.use_bottom_up && viewingPackage.cost_elements?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Cost Elements</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Element ID</TableHead>
                            <TableHead>Expense Type</TableHead>
                            <TableHead>Service Provider</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {viewingPackage.cost_elements.map((elem, idx) => (
                            <TableRow key={idx} className={['Profit', 'Contingency'].includes(elem.expense_type) ? 'bg-primary/5' : ''}>
                              <TableCell className="font-mono text-xs">{elem.element_id}</TableCell>
                              <TableCell>
                                <Badge variant={['Profit', 'Contingency'].includes(elem.expense_type) ? 'default' : 'outline'}>
                                  {elem.expense_type}
                                </Badge>
                              </TableCell>
                              <TableCell>{elem.service_provider || '-'}</TableCell>
                              <TableCell>{elem.location || '-'}</TableCell>
                              <TableCell className="text-right font-mono">
                                {viewingPackage.currency} {(elem.value || 0).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  {viewingPackage.status === 'draft' && canEdit('sales') && (
                    <>
                      <Button onClick={() => { setViewingPackage(null); openEditForm(viewingPackage); }}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Package
                      </Button>
                      <Button variant="outline" onClick={() => { handleStatusChange(viewingPackage.package_id, 'active'); setViewingPackage(null); }}>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Activate Package
                      </Button>
                    </>
                  )}
                  {viewingPackage.status === 'active' && canEdit('sales') && (
                    <Button variant="outline" onClick={() => { handleStatusChange(viewingPackage.package_id, 'completed'); setViewingPackage(null); }}>
                      <Archive className="w-4 h-4 mr-2" />
                      Mark as Completed
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => setViewingPackage(null)}>
                    Close
                  </Button>
                </div>
              </TabsContent>
              
              {/* Payments Tab */}
              <TabsContent value="payments" className="space-y-6">
                {loadingPayments ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    Loading payments...
                  </div>
                ) : (
                  <>
                    {/* Payment Summary Cards */}
                    <div className="grid grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-4">
                          <p className="text-xs text-muted-foreground uppercase">Total Amount</p>
                          <p className="text-2xl font-bold">
                            {paymentSummary?.currency || viewingPackage.currency} {(paymentSummary?.total_amount || viewingPackage.sale_value || 0).toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-green-600 uppercase">Amount Paid</p>
                          <p className="text-2xl font-bold text-green-700">
                            {paymentSummary?.currency || viewingPackage.currency} {(paymentSummary?.amount_paid || 0).toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-orange-50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-orange-600 uppercase">Balance Due</p>
                          <p className="text-2xl font-bold text-orange-700">
                            {paymentSummary?.currency || viewingPackage.currency} {(paymentSummary?.balance_due || viewingPackage.sale_value || 0).toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <p className="text-xs text-muted-foreground uppercase">Status</p>
                          <Badge className={`mt-2 ${getPaymentStatusColor(paymentSummary?.payment_status || 'Pending')}`}>
                            {paymentSummary?.payment_status || 'Pending'}
                          </Badge>
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Add Payment Button */}
                    {canEdit('sales') && (
                      <div className="flex justify-end">
                        <Button onClick={() => setShowAddPaymentDialog(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Record Payment
                        </Button>
                      </div>
                    )}
                    
                    {/* Payment History */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Receipt className="w-5 h-5" />
                          Payment History
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {paymentHistory.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Payment ID</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Method</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead className="w-20">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paymentHistory.map((payment) => (
                                <TableRow key={payment.payment_id}>
                                  <TableCell className="font-mono text-xs">{payment.payment_id}</TableCell>
                                  <TableCell>{new Date(payment.payment_date).toLocaleDateString()}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{payment.payment_method}</Badge>
                                  </TableCell>
                                  <TableCell className="text-right font-mono font-semibold text-green-600">
                                    +{viewingPackage.currency} {payment.amount.toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    {canEdit('sales') && (
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        onClick={() => handleDeletePayment(payment.payment_id)}
                                      >
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>No payments recorded yet</p>
                            <p className="text-sm mt-2">Click &quot;Record Payment&quot; to add a client payment</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Payment Dialog */}
      <Dialog open={showAddPaymentDialog} onOpenChange={setShowAddPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Record Client Payment
            </DialogTitle>
            <DialogDescription>
              Record a payment received from the client for this package
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount *</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="pl-9"
                />
              </div>
              {paymentSummary && (
                <p className="text-xs text-muted-foreground mt-1">
                  Balance due: {paymentSummary.currency} {paymentSummary.balance_due?.toLocaleString()}
                </p>
              )}
            </div>
            
            <div>
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Payment Method *</Label>
              <Select
                value={paymentForm.payment_method}
                onValueChange={(value) => setPaymentForm(prev => ({ ...prev, payment_method: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(method => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPaymentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPayment} disabled={addingPayment}>
              {addingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Record Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sales;
